// src/components/User/ShareModal.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  updateReviewer,
  disableReviewerShareCode,
  revokeReviewerSharedAccess,
  searchUserByIdentifier,
  directShareReviewer,
  getMyFriends,
} from "../../services/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const VISIBILITY_OPTIONS = [
  {
    value: "private",
    icon: "fa-lock",
    label: "Private",
    desc: "Only you can access this reviewer.",
  },
  {
    value: "code-only",
    icon: "fa-key",
    label: "Anyone with Code",
    desc: "Share a code and anyone can redeem a copy.",
  },
];

const ACTIVE_BORDER = {
  private: "border-slate-400 bg-slate-50 ring-1 ring-slate-300",
  "code-only": "border-blue-500 bg-blue-50 ring-1 ring-blue-200",
  public: "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200",
};

const ICON_COLOR = {
  private: "text-slate-500",
  "code-only": "text-blue-600",
  public: "text-emerald-600",
};

const TABS = [
  { id: "visibility", label: "Visibility", icon: "fa-eye" },
  { id: "code", label: "Share Code", icon: "fa-key" },
  { id: "direct", label: "Direct Share", icon: "fa-user-plus" },
];

const buildAvatar = (user) => {
  if (user?.avatar) return user.avatar;
  return `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(
    (user?.firstName || "") + (user?.lastName || "")
  )}&backgroundColor=b6e3f4`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const ShareModal = ({ note, onClose, onUpdateNote, onAlert }) => {
  const [activeTab, setActiveTab] = useState("visibility");

  // Visibility
  const [visibility, setVisibility] = useState(note?.visibility || "private");
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Code
  const [shareCode, setShareCode] = useState(note?.shareCode || null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [disablingCode, setDisablingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Direct share & Friends
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [friendsLoaded, setFriendsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [searchError, setSearchError] = useState("");

  const overlayRef = useRef(null);
  const reviewerId = note?._id || note?.id;
  const isOwned = !note?.isShared;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Visibility
  const handleVisibilityChange = async (val) => {
    if (val === "public" || val === visibility || savingVisibility) return;
    setSavingVisibility(true);
    try {
      await updateReviewer(reviewerId, { visibility: val });
      setVisibility(val);
      if (onUpdateNote) onUpdateNote({ ...note, visibility: val });
      onAlert({ type: "success", title: "Visibility Updated", message: `Set to "${val}".` });
    } catch (err) {
      onAlert({ type: "error", title: "Failed", message: err.message || "Could not update visibility." });
    } finally {
      setSavingVisibility(false);
    }
  };

  // Generate Code
  const handleGenerateCode = async () => {
    if (shareCode || generatingCode) return;
    const prefix = (note.title || note.topic || "NOTE")
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 4).toUpperCase().padEnd(4, "X");
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    const code = `${prefix}-${random}`;
    setGeneratingCode(true);
    try {
      await updateReviewer(reviewerId, { shareCode: code });
      setShareCode(code);
      if (onUpdateNote) onUpdateNote({ ...note, shareCode: code });
      onAlert({ type: "success", title: "Code Generated", message: "Share this code with others." });
    } catch (err) {
      onAlert({ type: "error", title: "Failed", message: err.message || "Could not generate code." });
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = async () => {
    if (!shareCode) return;
    try { await navigator.clipboard.writeText(shareCode); } catch { /* ignore */ }
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleDisableCode = async () => {
    if (!shareCode || disablingCode) return;
    setDisablingCode(true);
    try {
      await disableReviewerShareCode(reviewerId);
      setShareCode(null);
      if (onUpdateNote) onUpdateNote({ ...note, shareCode: null });
      onAlert({ type: "success", title: "Code Disabled", message: "New users can no longer redeem this code." });
    } catch (err) {
      onAlert({ type: "error", title: "Failed", message: err.message || "Could not disable code." });
    } finally {
      setDisablingCode(false);
    }
  };

  const handleRevokeAll = async () => {
    if (revoking) return;
    setRevoking(true);
    try {
      const res = await revokeReviewerSharedAccess(reviewerId);
      const count = Number(res?.revokedCount || 0);
      setShareCode(null);
      if (onUpdateNote) onUpdateNote({ ...note, shareCode: null });
      onAlert({
        type: "success", title: "Access Revoked",
        message: `Revoked ${count} shared ${count === 1 ? "copy" : "copies"} and disabled the code.`,
      });
    } catch (err) {
      onAlert({ type: "error", title: "Failed", message: err.message || "Could not revoke access." });
    } finally {
      setRevoking(false);
    }
  };

  // Load friends when modal mounts or Direct Share tab is activated
  useEffect(() => {
    if (activeTab === "direct" && !friendsLoaded) {
      let isSubscribed = true;
      setLoadingFriends(true);
      getMyFriends()
        .then((res) => {
          if (!isSubscribed) return;
          const list = res?.data || (Array.isArray(res) ? res : []);
          setFriends(list);
          setFriendsLoaded(true);
        })
        .catch((err) => {
          console.error("Failed to load friends for direct share:", err);
          if (isSubscribed) setFriendsLoaded(true);
        })
        .finally(() => {
          if (isSubscribed) setLoadingFriends(false);
        });
      return () => {
        isSubscribed = false;
      };
    }
  }, [activeTab, friendsLoaded]);

  // Live filtered friends based on search query
  const filteredFriends = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) => {
      const name = `${f.firstName || ""} ${f.lastName || ""}`.toLowerCase();
      const email = (f.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [friends, searchQuery]);

  const allFilteredFriendsSelected =
    filteredFriends.length > 0 &&
    filteredFriends.every((f) => selectedUserIds.has(f._id));

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allFilteredFriendsSelected) {
        filteredFriends.forEach((f) => next.delete(f._id));
      } else {
        filteredFriends.forEach((f) => next.add(f._id));
      }
      return next;
    });
  };

  // Direct Share Search for external users
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchError("Enter at least 2 characters."); return; }
    setSearchError("");
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await searchUserByIdentifier(q);
      const users = res?.users || [];
      const friendIdSet = new Set(friends.map((f) => String(f._id)));
      const nonFriendUsers = users.filter((u) => !friendIdSet.has(String(u._id)));
      if (users.length === 0) {
        setSearchError("No users found. Try a different name or email.");
      } else if (nonFriendUsers.length === 0 && filteredFriends.length > 0) {
        setSearchError("Matching users are already in your Friends list above.");
      }
      setSearchResults(nonFriendUsers);
    } catch (err) {
      setSearchError(err.message || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const handleSendDirect = async () => {
    if (selectedUserIds.size === 0 || sending) return;
    setSending(true);
    const targetIds = Array.from(selectedUserIds);
    try {
      if (targetIds.length === 1) {
        const targetId = targetIds[0];
        const targetObj =
          friends.find((f) => f._id === targetId) ||
          searchResults.find((u) => u._id === targetId);
        await directShareReviewer(reviewerId, targetId);
        onAlert({
          type: "success",
          title: "Reviewer Shared!",
          message: targetObj
            ? `Sent to ${targetObj.firstName} ${targetObj.lastName}.`
            : "Reviewer shared successfully.",
        });
      } else {
        const res = await directShareReviewer(reviewerId, targetIds);
        onAlert({
          type: "success",
          title: "Reviewers Shared!",
          message: res?.message || `Shared reviewer with ${targetIds.length} users.`,
        });
      }
      setSelectedUserIds(new Set());
      setSearchResults([]);
      setSearchQuery("");
    } catch (err) {
      onAlert({
        type: "error",
        title: "Failed",
        message: err.message || "Could not share reviewer.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg leading-tight">Share Reviewer</h2>
            <p className="text-blue-200 text-xs mt-0.5 truncate max-w-[260px]">
              {note?.title || note?.topic || "Untitled"}
            </p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-gray-100">
          {TABS.filter((tab) => isOwned || tab.id === "visibility").map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-xs font-bold flex flex-col items-center gap-1 transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-blue-600 border-blue-600 bg-blue-50/50"
                  : "text-gray-400 border-transparent hover:text-gray-600 hover:bg-gray-50"
              }`}
            >
              <i className={`fa-solid ${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab Body ── */}
        <div className="p-6 max-h-[440px] overflow-y-auto">

          {/* VISIBILITY */}
          {activeTab === "visibility" && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 mb-4">Control who can access this reviewer.</p>
              {VISIBILITY_OPTIONS.map((opt) => {
                const isActive = visibility === opt.value;
                return (
                  <button key={opt.value}
                    disabled={opt.disabled || savingVisibility}
                    onClick={() => handleVisibilityChange(opt.value)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                      isActive ? ACTIVE_BORDER[opt.value] : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                    } ${opt.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? "bg-white shadow-sm" : "bg-gray-100"}`}>
                      <i className={`fa-solid ${opt.icon} ${isActive ? ICON_COLOR[opt.value] : "text-gray-400"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${isActive ? "text-gray-900" : "text-gray-600"}`}>
                        {opt.label}
                        {opt.disabled && (
                          <span className="ml-2 text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-semibold uppercase">Soon</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                    </div>
                    {isActive && <i className={`fa-solid fa-circle-check ${ICON_COLOR[opt.value]} text-lg flex-shrink-0`} />}
                  </button>
                );
              })}
              {savingVisibility && (
                <p className="text-xs text-center text-blue-500 mt-2 animate-pulse">
                  <i className="fa-solid fa-circle-notch fa-spin mr-1" /> Saving…
                </p>
              )}
            </div>
          )}

          {/* SHARE CODE */}
          {activeTab === "code" && (
            <div className="space-y-5">
              <p className="text-xs text-gray-500">
                Generate a shareable code. Anyone who redeems it gets a personal copy of this reviewer.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                {shareCode ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Share Code</p>
                      <p className="text-2xl font-extrabold text-gray-900 tracking-widest font-mono">{shareCode}</p>
                    </div>
                    <button onClick={handleCopyCode}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition flex items-center gap-2 shadow-sm ${
                        codeCopied ? "bg-green-500 text-white" : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      <i className={`fa-solid ${codeCopied ? "fa-check" : "fa-copy"}`} />
                      {codeCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fa-solid fa-key text-3xl text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400 font-medium">No code generated yet</p>
                  </div>
                )}
              </div>

              {isOwned && (
                <div className="flex flex-col gap-2">
                  {!shareCode && (
                    <button onClick={handleGenerateCode} disabled={generatingCode}
                      className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-md shadow-blue-100">
                      {generatingCode
                        ? <><i className="fa-solid fa-circle-notch fa-spin" /> Generating…</>
                        : <><i className="fa-solid fa-wand-magic-sparkles" /> Generate Code</>}
                    </button>
                  )}
                  {shareCode && (
                    <button onClick={handleDisableCode} disabled={disablingCode}
                      className="w-full py-3 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 font-bold text-sm hover:bg-amber-100 transition flex items-center justify-center gap-2 disabled:opacity-60">
                      {disablingCode
                        ? <><i className="fa-solid fa-circle-notch fa-spin" /> Disabling…</>
                        : <><i className="fa-solid fa-ban" /> Disable Code</>}
                    </button>
                  )}
                  <button onClick={handleRevokeAll} disabled={revoking}
                    className="w-full py-3 rounded-xl bg-red-50 text-red-600 border border-red-200 font-bold text-sm hover:bg-red-100 transition flex items-center justify-center gap-2 disabled:opacity-60">
                    {revoking
                      ? <><i className="fa-solid fa-circle-notch fa-spin" /> Revoking…</>
                      : <><i className="fa-solid fa-user-slash" /> Revoke All Shared Copies</>}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* DIRECT SHARE */}
          {activeTab === "direct" && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Send this reviewer directly to friends. They'll receive a personal copy in their library.
              </p>

              {/* Search / Filter input */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchError("");
                      if (!e.target.value) {
                        setSearchResults([]);
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Filter friends or search name/email…"
                    className="w-full pl-9 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults([]);
                        setSearchError("");
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searching || searchQuery.trim().length < 2}
                  title="Search external users"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
                >
                  {searching ? (
                    <i className="fa-solid fa-circle-notch fa-spin" />
                  ) : (
                    <i className="fa-solid fa-search" />
                  )}
                </button>
              </div>

              {searchError && (
                <p className="text-xs text-red-500 flex items-center gap-1.5">
                  <i className="fa-solid fa-circle-exclamation" /> {searchError}
                </p>
              )}

              {/* Loading friends indicator */}
              {loadingFriends && (
                <div className="p-6 text-center bg-gray-50 rounded-xl border border-gray-100">
                  <i className="fa-solid fa-circle-notch fa-spin text-blue-600 text-lg mb-2" />
                  <p className="text-xs text-gray-500 font-medium">Loading your friends from Leaderboard…</p>
                </div>
              )}

              {/* Auto-shown Friends Section */}
              {!loadingFriends && friends.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                      <i className="fa-solid fa-user-group text-blue-600" />
                      All Friends ({friends.length})
                    </span>

                    {filteredFriends.length > 0 && (
                      <button
                        type="button"
                        onClick={handleToggleSelectAll}
                        className="text-xs font-bold text-blue-600 hover:text-blue-800 transition flex items-center gap-1"
                      >
                        <i className={`fa-solid ${allFilteredFriendsSelected ? "fa-square-check" : "fa-square"}`} />
                        {allFilteredFriendsSelected ? "Deselect All" : "Select All"}
                      </button>
                    )}
                  </div>

                  {filteredFriends.length === 0 ? (
                    <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center">
                      <p className="text-xs text-gray-500">
                        No friends match "{searchQuery}".
                      </p>
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={searching}
                        className="mt-2 text-xs font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        <i className="fa-solid fa-globe" /> Search outside friends list
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-0.5">
                      {filteredFriends.map((f) => {
                        const isSelected = selectedUserIds.has(f._id);
                        return (
                          <button
                            key={f._id}
                            type="button"
                            onClick={() => toggleUserSelection(f._id)}
                            className={`w-full text-left p-2.5 rounded-xl border-2 transition-all flex items-center gap-3 ${
                              isSelected
                                ? "border-blue-500 bg-blue-50/70 ring-1 ring-blue-200"
                                : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <img
                              src={buildAvatar(f)}
                              alt={f.firstName}
                              className="w-10 h-10 rounded-full bg-blue-100 border border-blue-200 object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-bold text-gray-800 truncate">
                                  {f.firstName} {f.lastName}
                                </p>
                                {f.level && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0">
                                    Lvl {f.level}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 truncate mt-0.5">{f.email}</p>
                            </div>
                            <div className="flex-shrink-0 pl-1">
                              {isSelected ? (
                                <i className="fa-solid fa-circle-check text-blue-600 text-lg" />
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-gray-400" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* No friends empty state */}
              {!loadingFriends && friends.length === 0 && (
                <div className="bg-gradient-to-br from-blue-50/70 to-indigo-50/50 border border-blue-100 rounded-2xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2">
                    <i className="fa-solid fa-user-plus text-base" />
                  </div>
                  <h4 className="text-xs font-bold text-gray-800">No friends added yet</h4>
                  <p className="text-[11px] text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">
                    Once you connect with friends in the Leaderboard (via Add / All / Friends), they will automatically appear here with their profile for quick sharing!
                  </p>
                </div>
              )}

              {/* External Search Results (if user searched for non-friends) */}
              {searchResults.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-gray-100">
                  <p className="text-xs font-bold text-gray-600">Other Users</p>
                  <div className="space-y-2 max-h-[160px] overflow-y-auto pr-0.5">
                    {searchResults.map((u) => {
                      const isSelected = selectedUserIds.has(u._id);
                      return (
                        <button
                          key={u._id}
                          type="button"
                          onClick={() => toggleUserSelection(u._id)}
                          className={`w-full text-left p-2.5 rounded-xl border-2 transition-all flex items-center gap-3 ${
                            isSelected
                              ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                              : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <img
                            src={buildAvatar(u)}
                            alt={u.firstName}
                            className="w-9 h-9 rounded-full bg-gray-100 object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">
                              {u.firstName} {u.lastName}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                          </div>
                          {isSelected ? (
                            <i className="fa-solid fa-circle-check text-blue-600 text-lg flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Button */}
              {selectedUserIds.size > 0 && (
                <div className="pt-2">
                  <button
                    onClick={handleSendDirect}
                    disabled={sending}
                    className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-md shadow-blue-100"
                  >
                    {sending ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin" /> Sending…
                      </>
                    ) : selectedUserIds.size === 1 ? (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send to{" "}
                        {(() => {
                          const singleId = Array.from(selectedUserIds)[0];
                          const target =
                            friends.find((f) => f._id === singleId) ||
                            searchResults.find((u) => u._id === singleId);
                          return target ? target.firstName : "Selected Friend";
                        })()}
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send to All Selected ({selectedUserIds.size})
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose}
            className="px-5 py-2 rounded-xl font-bold text-sm text-gray-500 hover:bg-gray-100 transition">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
